/* eslint-disable no-shadow */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';
import { useSelector } from 'react-redux';
import { oneOneOneReactQueryKeys } from 'screens/oneOnOneRabbit/InstancePage/constants/oneOnOneReactQueryKeys';
import { initiativeDrawerSelector } from 'selectors/initiativeDrawer';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

export const QUERY_KEYS = {
  INITIATIVE_DATA: 'INITIATIVE_DATA',
  INITIATIVE_COMMENTS: 'INITIATIVE_COMMENTS',
  INITIATIVE_SUBTASKS: 'INITIATIVE_SUBTASKS',
};

const updateInitiativeStatusApi = (body) => {
  const url = endpoints.analytics.initiative.updateInitiativeProgress(body.id);

  return sendRequest({
    method: 'PUT',
    url,
    body: {
      completionTS: body?.completionTS ?? null,
    },
    credentials: 'include',
  });
};

const updateInitiativeApi = (body) => {
  const url = endpoints.analytics.initiative.specificInitative(body.id);

  return sendRequest({
    method: 'PUT',
    url,
    body: {
      ...body,
    },
    credentials: 'include',
  });
};

const updateCompetencyInitiativeApi = (body) => {
  const url = endpoints.competency.updateTask(body.id);

  return sendRequest({
    method: 'PUT',
    url,
    body: {
      ...body,
    },
    credentials: 'include',
  });
};

const deleteInitiativeApi = (id) => {
  const url = endpoints.analytics.initiative.specificInitative(id);

  return sendRequest({
    method: 'DELETE',
    url,
    credentials: 'include',
  });
};

export const useDeleteInitiativeMutation = (uuid) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = [QUERY_KEYS.INITIATIVE_DATA, uuid];

  return useMutation({
    mutationFn: () => deleteInitiativeApi(uuid),
    onMutate: ({ updates }) => {
      queryClient.cancelQueries(queryKey);

      const previousInitiative = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (previousInitiative) => ({ ...previousInitiative, ...updates }));
      return { previousInitiative };
    },
    onError: (err, context) => {
      queryClient.setQueryData([queryKey], context.previousInitiative);
      toast({
        title: err.message || 'Initiative could not be deleted',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_DATA, uuid]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeCardsData]);
      queryClient.setQueryData([queryKey], data);
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneActionItems]);

      toast({
        title: `Initiative has been deleted successfully.`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
    },
  });
};

export const useUpdateInitiativeMutation = (uuid) => {
  const queryClient = useQueryClient();

  const toast = useToast();

  const queryKey = [QUERY_KEYS.INITIATIVE_DATA, uuid];

  const { taskType } = useSelector(initiativeDrawerSelector);

  return useMutation({
    mutationFn: ({ updates }) => {
      // eslint-disable-next-line no-shadow
      const previousInitiative = queryClient.getQueryData(queryKey);
      if (taskType === 'COMPETENCY_TASK') {
        return updateCompetencyInitiativeApi({ ...previousInitiative, ...updates });
      }
      return updateInitiativeApi({ ...previousInitiative, ...updates });
    },
    onMutate: ({ updates }) => {
      queryClient.cancelQueries(queryKey);

      const previousInitiative = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (previousInitiative) => ({ ...previousInitiative, ...updates }));
      return { previousInitiative };
    },
    onError: (err, newKR, context) => {
      queryClient.setQueryData([queryKey], context.previousInitiative);
      toast({
        title: err.message || 'Initiative could not be updated',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    },
    onSuccess: (data, prevData) => {
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeCardsData]);
      queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_DATA, uuid]);
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneActionItems]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 800);
      queryClient.setQueryData([queryKey], data);

      if (prevData.updates?.completionTS) {
        toast({
          title: `Initiative is marked as to be done`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
      }
      if (prevData.updates?.toIds) {
        toast({
          title: `Owners and collaborators has been updated successfully`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
      } else {
        toast({
          title: `Your changes have been saved`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
      }
    },
  });
};

export const useUpdateInitiativeStatusMutation = (uuid) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryKey = [QUERY_KEYS.INITIATIVE_DATA, uuid];

  const { taskType } = useSelector(initiativeDrawerSelector);

  return useMutation({
    mutationFn: ({ updates }) => {
      const previousInitiative = queryClient.getQueryData(queryKey);
      if (taskType === 'COMPETENCY_TASK') {
        return updateCompetencyInitiativeApi({ ...previousInitiative, ...updates });
      }
      return updateInitiativeStatusApi({ ...previousInitiative, ...updates });
    },
    onMutate: ({ updates }) => {
      queryClient.cancelQueries(queryKey);

      const previousInitiative = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (previousInitiative) => ({ ...previousInitiative, ...updates }));
      return { previousInitiative };
    },
    onError: (err, newKR, context) => {
      queryClient.setQueryData([queryKey], context.previousInitiative);

      toast({
        title: err.message || 'Initiative could not be updated',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_DATA, uuid]);
      queryClient.setQueryData([queryKey], data);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.InitiativeTableData]);
      queryClient.invalidateQueries([REACT_QUERY_KEYS.MyInitiativeTableData]);
      queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneActionItems]);

      toast({
        title: `Your changes have been saved`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
    },
  });
};
